import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function EmployeeFormComprehensive({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    first_name: '', middle_name: '', last_name: '',
    date_of_birth: '', age: '', gender: '', blood_group: '', marital_status: '',
    profile_photo_url: '', personal_email: '', official_email: '', mobile_number: '',
    alternate_mobile: '', current_address: '', permanent_address: '',
    date_of_joining: '', probation_end_date: '', employment_type: 'Full-time',
    employee_status: 'Active', departments: [], designation: '',
    reporting_manager_id: '', secondary_manager_id: '', work_location: '',
    office_branch: '', employee_category: '', shift_timing: '10:00 AM - 7:00 PM',
    work_mode: 'Office', confirmation_date: '', last_working_date: '',
    notice_period_days: 30, separation_reason: '', official_id_card_number: '',
    basic_salary: 0, gross_salary: 0, net_salary: 0, ctc_annual: 0, ctc_monthly: 0,
    hra: 0, da: 0, conveyance_allowance: 0, medical_allowance: 0,
    special_allowance: 0, other_allowances: 0, pf_employee: 0, pf_employer: 0,
    esi_employee: 0, esi_employer: 0, professional_tax: 0, income_tax_tds: 0,
    minimum_wage_applicable: false, minimum_wage_amount: 0, salary_adjustments: 0,
    arrears: 0, bonus: 0, incentives: 0, commission: 0,
    bank_name: '', account_number: '', ifsc_code: '', bank_branch: '', payment_mode: 'Bank Transfer',
    pan_number: '', aadhaar_number: '', pf_account_number: '', esi_number: '',
    passport_number: '', passport_expiry: '', driving_license_number: '', dl_expiry: '',
    voter_id: '', ration_card_number: '', gst_number: '',
    naps_registration_number: '', naps_status: '', naps_stipend_amount: 0,
    naps_start_date: '', naps_end_date: '', naps_certificate_number: '', naps_trade_occupation: '',
    nats_registration_number: '', nats_training_period: '', nats_trade_name: '',
    nats_stipend: 0, nats_certificate_status: '',
    emergency_contact_name_1: '', emergency_contact_relationship_1: '', emergency_contact_phone_1: '',
    emergency_contact_name_2: '', emergency_contact_relationship_2: '', emergency_contact_phone_2: '',
    emergency_address: '', emergency_email: '',
    highest_qualification: '', university_board: '', year_of_passing: '',
    specialization_stream: '', additional_certifications: '', professional_memberships: '',
    skills_expertise: '', languages_known: '',
    previous_company_name: '', previous_designation: '', previous_salary: 0,
    previous_employment_duration: '', reason_for_leaving: '',
    experience_letter_available: false, relieving_letter_available: false,
    total_years_of_experience: 0,
    resume_url: '', offer_letter_url: '', appointment_letter_url: '',
    contract_document_url: '', address_proof_url: '', photo_id_url: '',
    total_leave_balance: 9, sick_leave_balance: 0, casual_leave_balance: 0,
    leaves_taken_current_year: 0, total_present_days: 0, total_absent_days: 0,
    late_coming_count: 0, half_days_count: 0, last_attendance_date: '',
    last_appraisal_date: '', next_appraisal_due_date: '',
    current_performance_rating: '', last_increment_percentage: 0,
    last_increment_date: '', promotion_history: '', notes: '', internal_comments: ''
  });

  useEffect(() => {
    if (isEdit) fetchEmployee();
  }, [id]);

  const fetchEmployee = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData({...formData, ...response.data});
    } catch (error) {
      setError('Failed to fetch employee');
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDepartmentChange = (e) => {
    const departments = e.target.value.split(',').map(d => d.trim()).filter(d => d);
    setFormData(prev => ({ ...prev, departments }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (isEdit) {
        await axios.put(`${API}/employees/${id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/employees`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      navigate('/employees');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: '👤' },
    { id: 'employment', label: 'Employment', icon: '💼' },
    { id: 'compensation', label: 'Compensation', icon: '💰' },
    { id: 'govt_ids', label: 'Govt IDs', icon: '🆔' },
    { id: 'naps_nats', label: 'NAPS/NATS', icon: '📋' },
    { id: 'emergency', label: 'Emergency', icon: '🚨' },
    { id: 'education', label: 'Education', icon: '🎓' },
    { id: 'previous', label: 'Prev Employment', icon: '📝' },
    { id: 'documents', label: 'Documents', icon: '📄' },
    { id: 'attendance', label: 'Attendance', icon: '📅' },
    { id: 'performance', label: 'Performance', icon: '⭐' },
    { id: 'other', label: 'Other', icon: '📌' }
  ];

  const InputField = ({ label, name, type = 'text', required = false, ...props }) => (
    <div>
      <label className="block text-sm font-medium mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={formData[name] || ''}
        onChange={handleChange}
        required={required}
        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2b3380]"
        {...props}
      />
    </div>
  );

  const SelectField = ({ label, name, options, required = false }) => (
    <div>
      <label className="block text-sm font-medium mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        name={name}
        value={formData[name] || ''}
        onChange={handleChange}
        required={required}
        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#2b3380]"
      >
        <option value="">Select...</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  const CheckboxField = ({ label, name }) => (
    <div className="flex items-center">
      <input
        type="checkbox"
        name={name}
        checked={formData[name] || false}
        onChange={handleChange}
        className="w-4 h-4 text-[#2b3380] rounded mr-2"
      />
      <label className="text-sm font-medium">{label}</label>
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>
            {isEdit ? 'Edit Employee' : 'Add New Employee'} - 140+ Fields
          </h1>
          <button onClick={() => navigate('/employees')} className="px-4 py-2 rounded-lg border">
            ← Back
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6 overflow-x-auto">
          <div className="flex border-b">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium whitespace-nowrap flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-b-2 text-[#2b3380]'
                    : 'text-gray-600 hover:text-[#2b3380]'
                }`}
                style={activeTab === tab.id ? { borderColor: '#2b3380', color: '#2b3380' } : {}}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow-md p-6">
            
            {/* Basic Information */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Basic Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="First Name" name="first_name" required />
                  <InputField label="Middle Name" name="middle_name" />
                  <InputField label="Last Name" name="last_name" required />
                  <InputField label="Date of Birth" name="date_of_birth" type="date" />
                  <InputField label="Age" name="age" type="number" />
                  <SelectField label="Gender" name="gender" options={['Male', 'Female', 'Other']} />
                  <InputField label="Blood Group" name="blood_group" placeholder="e.g., O+, A+, B+" />
                  <SelectField label="Marital Status" name="marital_status" options={['Single', 'Married', 'Divorced', 'Widowed']} />
                  <InputField label="Profile Photo URL" name="profile_photo_url" />
                  <InputField label="Personal Email" name="personal_email" type="email" />
                  <InputField label="Official Email" name="official_email" type="email" required />
                  <InputField label="Mobile Number" name="mobile_number" type="tel" required />
                  <InputField label="Alternate Mobile" name="alternate_mobile" type="tel" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Current Address</label>
                    <textarea name="current_address" value={formData.current_address || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Permanent Address</label>
                    <textarea name="permanent_address" value={formData.permanent_address || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                </div>
              </div>
            )}

            {/* Employment Details */}
            {activeTab === 'employment' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Employment Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Date of Joining" name="date_of_joining" type="date" required />
                  <InputField label="Probation End Date" name="probation_end_date" type="date" />
                  <SelectField label="Employment Type" name="employment_type" options={['Full-time', 'Part-time', 'Contract', 'Intern', 'Apprentice']} required />
                  <SelectField label="Employee Status" name="employee_status" options={['Active', 'Inactive', 'Resigned', 'Terminated', 'On Leave']} />
                  <div>
                    <label className="block text-sm font-medium mb-1">Departments (comma-separated) *</label>
                    <input type="text" name="departments" value={formData.departments?.join(', ') || ''} onChange={handleDepartmentChange} placeholder="e.g., IT, HR" required className="w-full px-3 py-2 border rounded-lg" />
                  </div>
                  <InputField label="Designation" name="designation" required />
                  <InputField label="Reporting Manager ID" name="reporting_manager_id" />
                  <InputField label="Secondary Manager ID" name="secondary_manager_id" />
                  <InputField label="Work Location" name="work_location" />
                  <InputField label="Office Branch" name="office_branch" />
                  <InputField label="Employee Category" name="employee_category" />
                  <InputField label="Shift Timing" name="shift_timing" />
                  <SelectField label="Work Mode" name="work_mode" options={['Office', 'Remote', 'Hybrid']} />
                  <InputField label="Confirmation Date" name="confirmation_date" type="date" />
                  <InputField label="Last Working Date" name="last_working_date" type="date" />
                  <InputField label="Notice Period (days)" name="notice_period_days" type="number" />
                  <InputField label="Separation Reason" name="separation_reason" />
                  <InputField label="Official ID Card Number" name="official_id_card_number" />
                </div>
              </div>
            )}

            {/* Compensation & Salary */}
            {activeTab === 'compensation' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Compensation & Salary (₹)</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <InputField label="Basic Salary" name="basic_salary" type="number" step="0.01" required />
                  <InputField label="Gross Salary" name="gross_salary" type="number" step="0.01" />
                  <InputField label="Net Salary" name="net_salary" type="number" step="0.01" />
                  <InputField label="CTC Annual" name="ctc_annual" type="number" step="0.01" />
                  <InputField label="CTC Monthly" name="ctc_monthly" type="number" step="0.01" />
                  <InputField label="HRA" name="hra" type="number" step="0.01" />
                  <InputField label="DA" name="da" type="number" step="0.01" />
                  <InputField label="Conveyance Allowance" name="conveyance_allowance" type="number" step="0.01" />
                  <InputField label="Medical Allowance" name="medical_allowance" type="number" step="0.01" />
                  <InputField label="Special Allowance" name="special_allowance" type="number" step="0.01" />
                  <InputField label="Other Allowances" name="other_allowances" type="number" step="0.01" />
                  <InputField label="PF Employee" name="pf_employee" type="number" step="0.01" />
                  <InputField label="PF Employer" name="pf_employer" type="number" step="0.01" />
                  <InputField label="ESI Employee" name="esi_employee" type="number" step="0.01" />
                  <InputField label="ESI Employer" name="esi_employer" type="number" step="0.01" />
                  <InputField label="Professional Tax" name="professional_tax" type="number" step="0.01" />
                  <InputField label="Income Tax TDS" name="income_tax_tds" type="number" step="0.01" />
                  <InputField label="Minimum Wage Amount" name="minimum_wage_amount" type="number" step="0.01" />
                  <InputField label="Salary Adjustments" name="salary_adjustments" type="number" step="0.01" />
                  <InputField label="Arrears" name="arrears" type="number" step="0.01" />
                  <InputField label="Bonus" name="bonus" type="number" step="0.01" />
                  <InputField label="Incentives" name="incentives" type="number" step="0.01" />
                  <InputField label="Commission" name="commission" type="number" step="0.01" />
                </div>
                <div className="pt-4">
                  <CheckboxField label="Minimum Wage Applicable" name="minimum_wage_applicable" />
                </div>
                <h3 className="text-lg font-semibold mt-6 mb-3">Bank Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Bank Name" name="bank_name" />
                  <InputField label="Account Number" name="account_number" />
                  <InputField label="IFSC Code" name="ifsc_code" />
                  <InputField label="Bank Branch" name="bank_branch" />
                  <SelectField label="Payment Mode" name="payment_mode" options={['Bank Transfer', 'Cash', 'Cheque']} />
                </div>
              </div>
            )}

            {/* Government IDs & Compliance */}
            {activeTab === 'govt_ids' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Government IDs & Compliance</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="PAN Number" name="pan_number" />
                  <InputField label="Aadhaar Number" name="aadhaar_number" />
                  <InputField label="PF Account Number" name="pf_account_number" />
                  <InputField label="ESI Number" name="esi_number" />
                  <InputField label="Passport Number" name="passport_number" />
                  <InputField label="Passport Expiry" name="passport_expiry" type="date" />
                  <InputField label="Driving License Number" name="driving_license_number" />
                  <InputField label="DL Expiry" name="dl_expiry" type="date" />
                  <InputField label="Voter ID" name="voter_id" />
                  <InputField label="Ration Card Number" name="ration_card_number" />
                  <InputField label="GST Number" name="gst_number" />
                </div>
              </div>
            )}

            {/* NAPS & NATS Integration */}
            {activeTab === 'naps_nats' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">NAPS & NATS Integration</h2>
                <div className="bg-[#2b338015] border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-[#2b3380]">
                    🎓 Track National Apprenticeship Promotion Scheme (NAPS) and National Apprenticeship Training Scheme (NATS) enrollments
                  </p>
                </div>
                <h3 className="text-lg font-semibold">NAPS Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="NAPS Registration Number" name="naps_registration_number" />
                  <InputField label="NAPS Status" name="naps_status" placeholder="e.g., Active, Completed" />
                  <InputField label="NAPS Stipend Amount (₹)" name="naps_stipend_amount" type="number" step="0.01" />
                  <InputField label="NAPS Start Date" name="naps_start_date" type="date" />
                  <InputField label="NAPS End Date" name="naps_end_date" type="date" />
                  <InputField label="NAPS Certificate Number" name="naps_certificate_number" />
                  <InputField label="NAPS Trade/Occupation" name="naps_trade_occupation" />
                </div>
                <h3 className="text-lg font-semibold mt-6">NATS Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="NATS Registration Number" name="nats_registration_number" />
                  <InputField label="NATS Training Period" name="nats_training_period" placeholder="e.g., 6 months, 1 year" />
                  <InputField label="NATS Trade Name" name="nats_trade_name" />
                  <InputField label="NATS Stipend (₹)" name="nats_stipend" type="number" step="0.01" />
                  <InputField label="NATS Certificate Status" name="nats_certificate_status" placeholder="e.g., Issued, Pending" />
                </div>
              </div>
            )}

            {/* Emergency Contacts */}
            {activeTab === 'emergency' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Emergency Contacts</h2>
                <h3 className="text-lg font-semibold">Primary Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Contact Name 1" name="emergency_contact_name_1" />
                  <InputField label="Relationship 1" name="emergency_contact_relationship_1" />
                  <InputField label="Phone 1" name="emergency_contact_phone_1" type="tel" />
                </div>
                <h3 className="text-lg font-semibold mt-6">Secondary Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Contact Name 2" name="emergency_contact_name_2" />
                  <InputField label="Relationship 2" name="emergency_contact_relationship_2" />
                  <InputField label="Phone 2" name="emergency_contact_phone_2" type="tel" />
                </div>
                <h3 className="text-lg font-semibold mt-6">Additional</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Emergency Address</label>
                    <textarea name="emergency_address" value={formData.emergency_address || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                  <InputField label="Emergency Email" name="emergency_email" type="email" />
                </div>
              </div>
            )}

            {/* Education & Qualifications */}
            {activeTab === 'education' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Education & Qualifications</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Highest Qualification" name="highest_qualification" />
                  <InputField label="University/Board" name="university_board" />
                  <InputField label="Year of Passing" name="year_of_passing" type="number" />
                  <InputField label="Specialization/Stream" name="specialization_stream" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Additional Certifications</label>
                    <textarea name="additional_certifications" value={formData.additional_certifications || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Professional Memberships</label>
                    <textarea name="professional_memberships" value={formData.professional_memberships || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Skills & Expertise</label>
                    <textarea name="skills_expertise" value={formData.skills_expertise || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Languages Known</label>
                    <textarea name="languages_known" value={formData.languages_known || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="2" />
                  </div>
                </div>
              </div>
            )}

            {/* Previous Employment */}
            {activeTab === 'previous' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Previous Employment</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Previous Company Name" name="previous_company_name" />
                  <InputField label="Previous Designation" name="previous_designation" />
                  <InputField label="Previous Salary (₹)" name="previous_salary" type="number" step="0.01" />
                  <InputField label="Employment Duration" name="previous_employment_duration" placeholder="e.g., 2 years" />
                  <InputField label="Reason for Leaving" name="reason_for_leaving" />
                  <InputField label="Total Years of Experience" name="total_years_of_experience" type="number" step="0.1" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                  <CheckboxField label="Experience Letter Available" name="experience_letter_available" />
                  <CheckboxField label="Relieving Letter Available" name="relieving_letter_available" />
                </div>
              </div>
            )}

            {/* Documents */}
            {activeTab === 'documents' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Documents</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Resume URL" name="resume_url" />
                  <InputField label="Offer Letter URL" name="offer_letter_url" />
                  <InputField label="Appointment Letter URL" name="appointment_letter_url" />
                  <InputField label="Contract Document URL" name="contract_document_url" />
                  <InputField label="Address Proof URL" name="address_proof_url" />
                  <InputField label="Photo ID URL" name="photo_id_url" />
                </div>
              </div>
            )}

            {/* Leave & Attendance */}
            {activeTab === 'attendance' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Leave & Attendance Tracking</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Total Leave Balance" name="total_leave_balance" type="number" step="0.5" />
                  <InputField label="Sick Leave Balance" name="sick_leave_balance" type="number" step="0.5" />
                  <InputField label="Casual Leave Balance" name="casual_leave_balance" type="number" step="0.5" />
                  <InputField label="Leaves Taken (Current Year)" name="leaves_taken_current_year" type="number" step="0.5" />
                  <InputField label="Total Present Days" name="total_present_days" type="number" />
                  <InputField label="Total Absent Days" name="total_absent_days" type="number" />
                  <InputField label="Late Coming Count" name="late_coming_count" type="number" />
                  <InputField label="Half Days Count" name="half_days_count" type="number" />
                  <InputField label="Last Attendance Date" name="last_attendance_date" type="date" />
                </div>
              </div>
            )}

            {/* Performance & Reviews */}
            {activeTab === 'performance' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Performance & Reviews</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Last Appraisal Date" name="last_appraisal_date" type="date" />
                  <InputField label="Next Appraisal Due Date" name="next_appraisal_due_date" type="date" />
                  <InputField label="Current Performance Rating" name="current_performance_rating" />
                  <InputField label="Last Increment %" name="last_increment_percentage" type="number" step="0.01" />
                  <InputField label="Last Increment Date" name="last_increment_date" type="date" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Promotion History</label>
                  <textarea name="promotion_history" value={formData.promotion_history || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="3" />
                </div>
              </div>
            )}

            {/* Other/Notes */}
            {activeTab === 'other' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold mb-4">Notes & Comments</h2>
                <div>
                  <label className="block text-sm font-medium mb-1">General Notes</label>
                  <textarea name="notes" value={formData.notes || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="4" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Internal Comments (Not visible to employee)</label>
                  <textarea name="internal_comments" value={formData.internal_comments || ''} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg" rows="4" />
                </div>
              </div>
            )}

          </div>

          <div className="flex justify-end gap-4 mt-6">
            <button
              type="button"
              onClick={() => navigate('/employees')}
              className="px-6 py-3 rounded-lg font-medium border hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 rounded-lg text-white font-medium"
              style={{backgroundColor: loading ? '#6b7280' : '#2b3380'}}
            >
              {loading ? 'Saving...' : isEdit ? 'Update Employee' : 'Create Employee'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}

export default EmployeeFormComprehensive;
